import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import axios from "axios";
import { GlobalContext } from "../../context/GlobalContext";
import "./Download.css";
// import DatabaseDropdown from "../../components/dropdown/DatabaseDropdown";
import SelectInput from "../../components/select/SelectInput";
import PaginationModule from '../../pagination/PaginationModule';

//const PAGE_SIZE_OPTIONS = [10, 50, 100, 500];

const DownloadData = () => {
  const { API_URL, showToast } = useContext(GlobalContext);
  const tableRef = useRef(null);

  const [idsText, setIdsText] = useState("");

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  /* ========== REF ID MODAL ========= */
  const [showRefModal, setShowRefModal] = useState(false);
  const [selectedRefId, setSelectedRefId] = useState(null);
  const [refDetails, setRefDetails] = useState([]);
  const [refLoading, setRefLoading] = useState(false);

  // const [databaseOptions, setDatabaseOptions] = useState([]);
  const [domainOptions, setDomainOptions] = useState([]);
  const [urlOptions, setUrlOptions] = useState([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [isLoadingUrls, setIsLoadingUrls] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [selectedURL, setSelectedURL] = useState("");

  const [filterData, setFilterData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10);

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filterData.slice(indexOfFirstPost, indexOfLastPost);
  const howManyPages = Math.ceil(filterData.length / postsPerPage);

  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");

  /* ========== Sorting ========= */
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const handleSort = (colKey) => {
    if (!colKey) return;
    let direction = "asc";
    if (sortConfig.key === colKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: colKey, direction });
  };

  const sortedData = useMemo(() => {
    const sortedArray = [...currentPosts];

    if (sortColumn !== "") {
      sortedArray.sort((a, b) => {
        const valueA = a[sortColumn];
        const valueB = b[sortColumn];

        if (sortColumn === "lastmodified") {
          // Convert timestamps to Date objects for comparison
          const dateA = new Date(valueA).getTime();
          const dateB = new Date(valueB).getTime();

          if (dateA < dateB) return sortDirection === "asc" ? -1 : 1;
          if (dateA > dateB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        } else {
          if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
          if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        }
      });
    }

    return sortedArray;
  }, [currentPosts, sortColumn, sortDirection]);

  // Step 1: Load domains on component mount
  useEffect(() => {
    loadDomains();
  }, []);


  const loadDomains = async () => {
    setUrlOptions([]);
    setSelectedURL("");

    setIsLoadingDomains(true);
    try {
      const response = await axios.get(`${API_URL}/filterDomain`);
      setDomainOptions(
        response.data.map(x => ({
          label: x.domain,
          value: x.domain
        }))
      );
      setUrlOptions([]);
      setSelectedURL("");
    } catch (error) {
      console.log(error);
      showToast('error', 'Failed to load domains', 'top', 5);
      setDomainOptions([]);
    } finally {
      setIsLoadingDomains(false);
    }
  };

  // Step 2: Load URLs when domain is selected
  const loadURLs = async (domain) => {
    if (!domain) {
      setUrlOptions([]);
      setSelectedURL("");
      return;
    }

    setIsLoadingUrls(true);
    try {
      const response = await axios.get(`${API_URL}/sourceURLs/${encodeURIComponent(domain)}`
      );
      const urlList = response.data.map(x => ({
        label: x.cur_url,
        value: x.cur_url
      }));
      const allurlList = [{ label: "All", value: "all" }, ...urlList,];
      setUrlOptions(allurlList);
      setSelectedURL("");
    } catch (error) {
      console.log(error);
      showToast('error', 'Failed to load URLs', 'top', 5);
      setUrlOptions([]);
    } finally {
      setIsLoadingUrls(false);
    }
  };

  const handleDomainChange = (selectedOption) => {
    const domain = selectedOption ? selectedOption.value : '';
    setSelectedDomain(domain);
    loadURLs(domain);
  };

  const handleURLChange = (selectedOption) => {
    setSelectedURL(selectedOption ? selectedOption.value : '');
  };


  /* ---------------- Fetch Data ---------------- */
  const fetchData = async () => {
    setFilterData([])
    setData([])
    if (!selectedDomain) {
      showToast("error", "Domain is required");
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/downlaodSource`, {
        domain: selectedDomain,
        cur_url: selectedURL || null,
        refids: idsText || null,
      });

      setData(response.data);
      setFilterData(response.data);
      setCurrentPage(1); // Reset the current page to 1

    } catch {
      showToast("error", "Search failed");
    } finally {
      setIsLoading(false);
    }
  };


  /* ---------------- Download ---------------- */
  const handleDownload = async (format) => {
    try {
      const res = await axios.post(`${API_URL}/DownloadData`,
        {
          domain: selectedDomain,
          cur_url: selectedURL || null,
          refids: idsText || null,
          format,
        },
        {
          responseType: "blob",
        }
      );

      // --------------------------------------------------
      // Generate filename from selectedURL
      // --------------------------------------------------
      let filename = "main";

      if (selectedURL && selectedURL.trim()) {
        let url = selectedURL.trim();

        // Remove protocol
        url = url.replace(/^https?:\/\//i, "");

        // Remove FSSAI domain
        url = url.replace(/^fssai\.gov\.in\/?/i, "");

        // Convert URL path to filename
        url = url
          .replace(/\//g, "_")
          .replace(/\\/g, "_")
          .replace(/\s+/g, "-")
          .replace(/[<>:"/\\|?*]/g, "_")
          .replace(/_+/g, "_")
          .replace(/-+/g, "-")
          .replace(/^[_\-.]+|[_\-.]+$/g, "");

        if (url) {
          filename = url;
        }
      }

      // --------------------------------------------------
      // Correct extension
      // --------------------------------------------------
      const extension =
        format === "excel"
          ? "xlsx"
          : format === "csv"
            ? "csv"
            : format === "pdf"
              ? "pdf"
              : format;

      filename = `${filename}.${extension}`;

      // --------------------------------------------------
      // Download
      // --------------------------------------------------
      const blobUrl = window.URL.createObjectURL(res.data);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setShowDownloadMenu(false);

    } catch (error) {
      console.error("Download failed:", error);
      showToast("error", "Download failed");
    }
  };


  /* ---------------- Column Resize ---------------- */
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    table.querySelectorAll("th").forEach((th) => {
      if (th.querySelector(".resizer")) return;

      const resizer = document.createElement("span");
      resizer.className = "resizer";
      th.style.position = "relative";
      th.appendChild(resizer);

      let startX, startWidth;

      resizer.onmousedown = (e) => {
        startX = e.clientX;
        startWidth = th.offsetWidth;

        document.onmousemove = (e) => {
          th.style.width = startWidth + (e.clientX - startX) + "px";
        };

        document.onmouseup = () => {
          document.onmousemove = null;
          document.onmouseup = null;
        };
      };
    });
  }, [data]);

  /* ---------------- RefID Modal Logic ---------------- */
  const openRefDetails = async (r) => {
    setShowRefModal(true);
    setRefLoading(true);

    try {
      // const res = await axios.get(`${API_URL}/TranslationByRefId/${selectedDatabase}/${refid}`);
      setRefDetails(r || []);
    } catch {
      setRefDetails([]);
    } finally {
      setRefLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      {/* ================= Filters ================= */}

      <div className="row g-2 align-items-end">
        {/* Domain - FULL WIDTH */}
        <div className="col-md-3">
          <label className="form-label">Select Domain</label>
          <div className="w-100">
            <SelectInput
              options={domainOptions}
              value={domainOptions.find(o => o.value === selectedDomain) || null}
              onChange={handleDomainChange}
              placeholder={isLoadingDomains ? "Loading domains..." : "Search domain..."}
              noOptionsMessage={() => isLoadingDomains ? 'No domains found' : 'Select domain first'}
              isDisabled={!selectedDatabase || isLoadingDomains}
              isLoading={isLoadingDomains}
            />
          </div>
        </div>
        <div className="col-md-4">
          <label className="form-label">Select Current URL</label>
          <div className="w-100">
            <SelectInput
              options={urlOptions}
              value={urlOptions.find((o) => o.value === selectedURL) || null}
              onChange={handleURLChange}
              placeholder={selectedDomain ? 'Search URL...' : 'Select domain first'}
              isDisabled={!selectedDomain || isLoadingUrls}
              noOptionsMessage={() => selectedDomain ? 'No URL found' : 'Select domain first'}
              isLoading={isLoadingUrls}
            />
          </div>
        </div>

        <div className="col-md-3">
          <label className="form-label">Enter ref. IDs (optional)</label>
          <input
            className="form-control"
            placeholder="220,221 or 220-222"
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
          />
        </div>

        <div className="col-md-2 d-flex gap-2">
          <button
            className="btn btn-primary w-50"
            onClick={() => fetchData(1)}
            disabled={isLoading}
            style={{ height: "38px" }}
          >
            Search
          </button>

          <div className="position-relative w-50">
            <button
              className="btn btn-success w-100"
              disabled={!data.length}
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              style={{ height: "38px" }}
            >
              Download
            </button>

            {showDownloadMenu && (
              <div className="dropdown-menu show">
                <button className="dropdown-item" onClick={() => handleDownload("csv")}>CSV</button>
                <button className="dropdown-item" onClick={() => handleDownload("excel")}>Excel</button>
                <button className="dropdown-item" onClick={() => handleDownload("pdf")}>PDF</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= Table ================= */}
      {currentPosts.length > 0 ? (
        <div className="mt-3">
          <table ref={tableRef} className="table table-bordered table-striped resizable-table">
            <thead className="table-light">
              <tr>
                <th className="width-7p" onClick={() => handleSort("id")} style={{ cursor: "pointer" }}>
                  ID {sortConfig.key === "id" ? (sortConfig.direction === "asc" ? " 🔼" : " 🔽") : ""}
                </th>
                <th className="width-7p" onClick={() => handleSort("refid")} style={{ cursor: "pointer" }}>
                  Ref ID {sortConfig.key === "refid" ? (sortConfig.direction === "asc" ? " 🔼" : " 🔽") : ""}
                </th>
                <th className="width-25p" onClick={() => handleSort("cur_url")} style={{ cursor: "pointer" }}>
                  Cur URL {sortConfig.key === "cur_url" ? (sortConfig.direction === "asc" ? " 🔼" : " 🔽") : ""}
                </th>
                <th className="width-25p" onClick={() => handleSort("txtmd5")} style={{ cursor: "pointer" }}>
                  Clean MD5 {sortConfig.key === "txtmd5" ? (sortConfig.direction === "asc" ? " 🔼" : " 🔽") : ""}
                </th>
                <th onClick={() => handleSort("finaltxt")} style={{ cursor: "pointer" }}>
                  Final Text {sortConfig.key === "srctxt" ? (sortConfig.direction === "asc" ? " 🔼" : " 🔽") : ""}
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedData.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => openRefDetails(r)}>
                      {r.refid}
                    </span>
                  </td>
                  <td>{r.cur_url}</td>
                  <td>{r.txtmd5}</td>
                  <td>{r.srctxt}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ================= Pagination ================= */}

          <PaginationModule totalPages={howManyPages} postsPerPage={postsPerPage} setPostsPerPage={setPostsPerPage} currentPage={currentPage} setCurrentPage={setCurrentPage} />

        </div>
      ) : (
        <div className="container-fluid text-center py-4 text-muted border mt-2">
          {isLoading ? "Please wait while processing..." : "Data not found."}
        </div>
      )}



      {/* ================= RefID Modal ================= */}
      {showRefModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5>Ref ID Details: {selectedRefId}</h5>
                  <button className="btn-close" onClick={() => setShowRefModal(false)} />
                </div>
                <div className="modal-body">
                  {refLoading ? (
                    "Loading..."
                  ) : !refDetails || Object.keys(refDetails).length === 0 ? (
                    "No data found"
                  ) : (
                    <table className="table table-bordered table-striped">
                      <tbody>
                        {Object.entries(refDetails).map(([key, value]) => (
                          <tr key={key}>
                            <th style={{ width: "30%" }}>{key}</th>
                            <td>{value ?? "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default DownloadData;
